import {useCallback, useEffect, useState} from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import {Send as SendIcon} from "@mui/icons-material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "../../components/SoftInput";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";

import DataTable from "../../components/DataTable";
import EnhancedTable from "../../components/EnhancedTable";
import {getAllUsers} from "../../api/user/userApiClient";
import {sendNotification} from "../../api/notification/notificationApiClient";
import {handleApiError, showSuccess} from "../../utils/notificationUtils";

/**
 * SYSTEM_ADMIN demo page.
 *
 *   Left side  — every user in the system, searchable.
 *   Right side — title + body form. Picks one user from the table
 *                and "Send" pushes the notification. The recipient
 *                gets it live on their web dashboard (if logged in)
 *                and on their mobile app (when Phase 3 / 4 are
 *                wired). Backend persists the record either way.
 */
function NotificationsDemo() {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);

    const loadUsers = useCallback(async () => {
        try {
            setIsLoading(true);
            const res = await getAllUsers();
            const list = res?.data || [];
            setUsers(list);
            setFilteredUsers(list);
        } catch (err) {
            handleApiError(err, "Failed to load users");
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers().then();
    }, [loadUsers]);

    const handleFilter = (value) => {
        const q = value.trim().toLowerCase();
        if (!q) {
            setFilteredUsers(users);
            return;
        }
        setFilteredUsers(
            users.filter((u) => {
                const hay = [u.firstName, u.lastName, u.email, u.phoneNumber, u.userRole]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return hay.includes(q);
            }),
        );
    };

    const handleSend = async () => {
        if (!selected || !title.trim() || !body.trim()) return;
        try {
            setSending(true);
            await sendNotification({
                recipientUserId: selected.id,
                title: title.trim(),
                body: body.trim(),
                type: "DEMO",
            });
            showSuccess(`Sent to ${selected.firstName} ${selected.lastName}`);
            setTitle("");
            setBody("");
        } catch (err) {
            handleApiError(err, "Failed to send notification");
        } finally {
            setSending(false);
        }
    };

    const userColumns = [
        {
            name: "name",
            value: "firstName",
            align: "left",
            width: "25",
            renderCell: (row) => (
                <SoftTypography
                    variant="button"
                    fontWeight={selected?.id === row.id ? "bold" : "regular"}
                    color={selected?.id === row.id ? "primary" : "dark"}
                    sx={{display: "inline-block", width: "max-content"}}
                >
                    {row.firstName} {row.lastName}
                </SoftTypography>
            ),
        },
        {name: "email", value: "email", align: "left", width: "30"},
        {name: "role", value: "userRole", align: "left", width: "15"},
        {
            name: "",
            value: "action",
            align: "right",
            width: "10",
            renderCell: (row) => (
                <SoftButton
                    variant={selected?.id === row.id ? "gradient" : "outlined"}
                    color="primary"
                    size="small"
                    onClick={() => setSelected(row)}
                >
                    {selected?.id === row.id ? "Selected" : "Select"}
                </SoftButton>
            ),
        },
    ];

    const canSend = !!selected && title.trim() && body.trim() && !sending;

    return (
        <DashboardLayout>
            <SoftBox pt={2} px={2} width="100%">
                <SoftBox
                    pt={2}
                    px={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    gap={2}
                >
                    <SoftBox display="flex" alignItems="center" gap={2} flexShrink={0}>
                        <SoftTypography variant="h6" fontWeight="medium">
                            Send Notification (Demo)
                        </SoftTypography>
                    </SoftBox>
                    <SoftBox flexGrow={1} maxWidth={560}>
                        <SoftInput
                            placeholder="Filter users by name, email, phone or role"
                            icon={{component: "search", direction: "left"}}
                            onChange={(e) => handleFilter(e.target.value)}
                        />
                    </SoftBox>
                </SoftBox>
            </SoftBox>

            <SoftBox p={2} width="100%">
                <Grid container spacing={3}>
                    {/* User picker */}
                    <Grid item xs={12} lg={7}>
                        <EnhancedTable
                            TableComponent={DataTable}
                            isLoading={isLoading}
                            data={filteredUsers}
                            loadingMessage="Loading users..."
                            noDataMessage="No users found"
                            columns={userColumns}
                        />
                    </Grid>

                    {/* Compose pane */}
                    <Grid item xs={12} lg={5}>
                        <Card sx={{p: 3}}>
                            <SoftTypography variant="h6" fontWeight="bold" color="dark" mb={0.5}>
                                Compose
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text" mb={2}>
                                Pick a user from the table, then fill in the message.
                            </SoftTypography>

                            <SoftBox
                                mt={2}
                                mb={2}
                                p={1.5}
                                sx={({palette, borders}) => ({
                                    borderRadius: borders.borderRadius.md,
                                    border: `1px dashed ${palette.grey[300]}`,
                                    backgroundColor: palette.grey[100],
                                })}
                            >
                                {selected ? (
                                    <SoftTypography variant="caption" color="dark">
                                        To: <strong>{selected.firstName} {selected.lastName}</strong>{" "}
                                        ({selected.email}) — {selected.userRole}
                                    </SoftTypography>
                                ) : (
                                    <SoftTypography variant="caption" color="text" opacity={0.7}>
                                        No recipient selected yet.
                                    </SoftTypography>
                                )}
                            </SoftBox>

                            <SoftBox mb={2}>
                                <SoftTypography variant="caption" fontWeight="bold" color="text">
                                    Title
                                </SoftTypography>
                                <SoftInput
                                    placeholder="e.g. Pickup reminder"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </SoftBox>

                            <SoftBox mb={2}>
                                <SoftTypography variant="caption" fontWeight="bold" color="text">
                                    Message
                                </SoftTypography>
                                <SoftInput
                                    placeholder="Body of the notification"
                                    multiline
                                    rows={4}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                />
                            </SoftBox>

                            <SoftButton
                                variant="gradient"
                                color="primary"
                                fullWidth
                                onClick={handleSend}
                                disabled={!canSend}
                                startIcon={<SendIcon />}
                            >
                                {sending ? "Sending..." : "Send notification"}
                            </SoftButton>
                        </Card>
                    </Grid>
                </Grid>
            </SoftBox>
        </DashboardLayout>
    );
}

export default NotificationsDemo;
